import { EdgelessLegacySlotIdentifier } from '@blocksuite/affine-block-surface';
import {
  focusElementsOnScreen,
  getRenderXYWH,
  getSelectedRect,
} from '@blocksuite/affine-shared/utils';
import { deserializeXYWH, type IVec } from '@blocksuite/global/gfx';
import type { PointerEventState } from '@blocksuite/std';
import {
  cancelGfxViewportFrame,
  getTopElements,
  GfxControllerIdentifier,
  type GfxModel,
  InteractivityIdentifier,
  isGfxGroupCompatibleModel,
  scheduleGfxViewportFrame,
  type ToolOptionWithType,
} from '@blocksuite/std/gfx';
import { effect } from '@preact/signals-core';

import type { AffineDragHandleWidget } from '../drag-handle.js';
import {
  EDGELESS_HANDLE_QUERY_RADIUS,
  type EdgelessDragHandleKind,
  type EdgelessDragHandleSlot,
  type EdgelessDragHandleTarget,
  isPointInEdgelessHandleZone,
  resolveEdgelessDragHandleAnchor,
  resolveEdgelessDragHandleMoveIds,
  resolveEdgelessDragHandleVisibility,
} from '../edgeless-handle-contract.js';

/** Owns the two top-level canvas move affordances: selection and direct hover. */
export class EdgelessWatcher {
  private _activeDragKind: EdgelessDragHandleKind | null = null;

  private _hoveredElementId: string | null = null;

  private _lastPointerClient: IVec | null = null;

  private _toolName: string | null = null;

  private readonly _handleEdgelessToolUpdated = (
    newTool: ToolOptionWithType
  ) => {
    this._toolName = newTool.toolType?.toolName ?? null;
    this._syncHandles();
  };

  private readonly _handleEdgelessViewPortUpdated = ({
    zoom,
    center,
  }: {
    zoom: number;
    center: IVec;
  }) => {
    if (this.widget.scale.peek() !== zoom) {
      this.widget.scale.value = zoom;
    }

    if (
      this.widget.center[0] !== center[0] ||
      this.widget.center[1] !== center[1]
    ) {
      this.widget.center = [...center];
    }

    this._scheduleHandles();
  };

  private _clearSlot(kind: EdgelessDragHandleKind) {
    const current =
      kind === 'selection'
        ? this.widget.edgelessSelectionHandle
        : this.widget.edgelessHoverHandle;
    if (current.visible) this._setSlot(kind, { ...current, visible: false });
  }

  clearHiddenTarget(kind: EdgelessDragHandleKind) {
    const slot =
      kind === 'selection'
        ? this.widget.edgelessSelectionHandle
        : this.widget.edgelessHoverHandle;
    if (!slot.visible && slot.target && this._activeDragKind !== kind) {
      this._setSlot(kind, { target: null, visible: false });
    }
  }

  private _expandMovingElements(models: readonly GfxModel[]) {
    const moving = new Set<GfxModel>();
    getTopElements([...models]).forEach(model => {
      moving.add(model);
      if (isGfxGroupCompatibleModel(model)) {
        model.descendantElements.forEach(descendant => moving.add(descendant));
      }
    });
    return [...moving];
  }

  private _getHoverTarget(): EdgelessDragHandleTarget | null {
    if (!this._hoveredElementId) return null;
    const element = this.gfx.getElementById<GfxModel>(this._hoveredElementId);
    if (!element || element.isLocked()) return null;
    return this._toTarget('hover', [element]);
  }

  private _getSelectionTarget(): EdgelessDragHandleTarget | null {
    const { selection } = this.gfx;
    const elements = getTopElements(selection.selectedElements);
    if (
      selection.inoperable ||
      elements.length === 0 ||
      elements.some(element => element.isLocked())
    ) {
      return null;
    }
    return this._toTarget('selection', elements);
  }

  private _isPresentation() {
    return this._toolName === 'frameNavigator' || this.gfx.selection.inoperable;
  }

  private _isUnavailable() {
    return (
      this.widget.store.readonly ||
      this.widget.mode === 'page' ||
      this._toolName !== 'default' ||
      this._isPresentation()
    );
  }

  private _setSlot(kind: EdgelessDragHandleKind, slot: EdgelessDragHandleSlot) {
    if (kind === 'selection') {
      this.widget.edgelessSelectionHandle = slot;
    } else {
      this.widget.edgelessHoverHandle = slot;
    }
  }

  private _showSlot(
    kind: EdgelessDragHandleKind,
    target: EdgelessDragHandleTarget
  ) {
    this._setSlot(kind, { target, visible: true });
  }

  private _selectionOwnsHandle() {
    const elements = this.gfx.selection.selectedElements;
    return (
      this._activeDragKind !== 'hover' &&
      (elements.length > 1 ||
        elements.some(element => isGfxGroupCompatibleModel(element)))
    );
  }

  private readonly _scheduleHandles = () => {
    if (!this.widget.isConnected) return;
    // Only local affordance geometry is deferred. Model/drag-preview/presence
    // writers keep their existing pointer cadence for local and remote viewers.
    scheduleGfxViewportFrame(this.widget.std, 'dom', this, this._syncHandles);
  };

  private readonly _syncHandles = () => {
    cancelGfxViewportFrame(this.widget.std, 'dom', this);
    if (!this.widget.isConnected || this._isUnavailable()) {
      this._clearSlot('selection');
      this._clearSlot('hover');
      return;
    }

    const selectionTarget = this._getSelectionTarget();
    const hoverTarget = this._selectionOwnsHandle()
      ? null
      : this._getHoverTarget();
    const visibility = resolveEdgelessDragHandleVisibility({
      draggingKind: this._activeDragKind,
      hoverTarget,
      presentation: this._isPresentation(),
      selectionTarget,
    });

    if (visibility.selection && selectionTarget) {
      this._showSlot('selection', selectionTarget);
    } else {
      this._clearSlot('selection');
    }

    if (visibility.hover && hoverTarget) {
      this._showSlot('hover', hoverTarget);
    } else {
      this._clearSlot('hover');
    }
  };

  private _toTarget(
    kind: EdgelessDragHandleKind,
    elements: readonly GfxModel[]
  ): EdgelessDragHandleTarget {
    const rect = getSelectedRect([...elements]);
    const [left, top] = this.gfx.viewport.toViewCoord(rect.left, rect.top);
    const zoom = this.gfx.viewport.zoom;
    const elementIds = elements.map(element => element.id);
    let modelAnchor: { left: number; rotate: number; top: number };
    if (elements.length === 1) {
      const [elementLeft, elementTop, width, height] = deserializeXYWH(
        getRenderXYWH(elements[0])
      );
      modelAnchor = resolveEdgelessDragHandleAnchor({
        height,
        left: elementLeft,
        rotate: elements[0].rotate ?? 0,
        top: elementTop,
        width,
      });
    } else {
      modelAnchor = {
        left: rect.left + rect.width / 2,
        rotate: 0,
        top: rect.top,
      };
    }
    const [anchorLeft, anchorTop] = this.gfx.viewport.toViewCoord(
      modelAnchor.left,
      modelAnchor.top
    );

    return {
      anchor: {
        left: anchorLeft,
        rotate: modelAnchor.rotate,
        top: anchorTop,
      },
      elementIds,
      key: [...elementIds].sort().join('\0'),
      kind,
      rect: {
        height: rect.height * zoom,
        left,
        top,
        width: rect.width * zoom,
      },
    };
  }

  private readonly _updateHoverFromPointer = (state: PointerEventState) => {
    if (this._activeDragKind) return true;

    const raw = state.raw;
    this._lastPointerClient = [raw.x, raw.y];
    if (
      raw.composedPath().some(target => {
        return (
          target instanceof HTMLElement &&
          target.classList.contains('affine-edgeless-drag-handle')
        );
      })
    ) {
      return true;
    }

    if (this._isUnavailable() || this._selectionOwnsHandle()) {
      if (!this._hoveredElementId && !this.widget.edgelessHoverHandle.visible)
        return;
      this._hoveredElementId = null;
      this._syncHandles();
      return;
    }

    const nextId = this._resolveHoverAtClient([raw.x, raw.y]);
    if (nextId !== this._hoveredElementId) {
      this._hoveredElementId = nextId;
      this._syncHandles();
    }
    return;
  };

  get gfx() {
    return this.widget.std.get(GfxControllerIdentifier);
  }

  constructor(readonly widget: AffineDragHandleWidget) {}

  startDrag(kind: EdgelessDragHandleKind, event: PointerEvent) {
    if (
      event.button !== 0 ||
      !event.isPrimary ||
      this._activeDragKind ||
      this._isUnavailable()
    ) {
      return;
    }

    this._syncHandles();
    const slot =
      kind === 'selection'
        ? this.widget.edgelessSelectionHandle
        : this.widget.edgelessHoverHandle;
    const target = slot.visible ? slot.target : null;
    if (!target) return;

    const ids = resolveEdgelessDragHandleMoveIds({ kind, target });
    const models = ids
      .map(id => this.gfx.getElementById<GfxModel>(id))
      .filter((model): model is GfxModel => model !== null);
    if (models.length === 0 || models.some(model => model.isLocked())) return;

    event.preventDefault();
    event.stopPropagation();
    this.widget.anchorBlockId.value = null;
    this.widget.activeDragHandle = null;
    this._activeDragKind = kind;

    if (kind === 'hover') {
      this.gfx.selection.set({
        editing: false,
        elements: models.map(model => model.id),
      });
    }
    this._syncHandles();

    const interactivity = this.widget.std.getOptional(InteractivityIdentifier);
    if (!interactivity) {
      this._activeDragKind = null;
      this._syncHandles();
      return;
    }

    this.widget.store.captureSync();
    interactivity.handleElementMove({
      event,
      movingElements: this._expandMovingElements(models),
      onDragEnd: () => {
        this.widget.store.captureSync();
        this._activeDragKind = null;
        this._refreshHoverFromLastPointer();
        this._syncHandles();
      },
    });
  }

  fitTargetToScreen(kind: EdgelessDragHandleKind, event: MouseEvent) {
    if (
      event.button !== 0 ||
      event.shiftKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      this._isUnavailable()
    ) {
      return;
    }
    const target = (
      kind === 'selection'
        ? this.widget.edgelessSelectionHandle
        : this.widget.edgelessHoverHandle
    ).target;
    if (!target) return;

    event.preventDefault();
    event.stopPropagation();

    const elements = target.elementIds
      .map(id => this.gfx.getElementById<GfxModel>(id))
      .filter((model): model is GfxModel => model !== null);
    if (elements.length === 0) return;
    // A double click on the handle selects its element and brings it into focus.
    this.gfx.selection.set({
      editing: false,
      elements: elements.map(model => model.id),
    });
    this._syncHandles();
    focusElementsOnScreen(this.gfx, elements);
  }

  private _refreshHoverFromLastPointer() {
    if (this._selectionOwnsHandle()) {
      this._hoveredElementId = null;
      return;
    }
    if (!this._lastPointerClient) return;
    this._hoveredElementId = this._resolveHoverAtClient(
      this._lastPointerClient
    );
  }

  private _resolveHoverAtClient(client: IVec): string | null {
    const { viewport } = this.gfx;
    const [x, y] = viewport.toModelCoordFromClientCoord(client);
    const direct = this.gfx.getElementByPoint(x, y);

    const point = viewport.toViewCoordFromClientCoord(client);
    const radius = EDGELESS_HANDLE_QUERY_RADIUS / viewport.zoom;
    const candidates = this.gfx.getElementsByBound({
      x: x - radius,
      y: y - radius,
      w: radius * 2,
      h: radius * 2,
    });
    for (const element of candidates.reverse()) {
      // A containing frame may cover the empty handle zone. Honor the paint
      // order so its children can activate, without reaching through a front object.
      if (direct && this.gfx.layer.compare(element, direct) <= 0) break;
      if (element.isLocked()) continue;
      const target = this._toTarget('hover', [element]);
      if (isPointInEdgelessHandleZone(point, target.anchor)) return element.id;
    }
    return direct && !direct.isLocked() ? direct.id : null;
  }

  updateAnchorElement = () => {
    this._syncHandles();
  };

  watch() {
    if (this.widget.mode === 'page') return;

    const { disposables, std } = this.widget;
    const { viewport, selection, tool, surface } = this.gfx;
    const edgelessSlots = std.get(EdgelessLegacySlotIdentifier);

    disposables.add(
      viewport.viewportUpdated.subscribe(this._handleEdgelessViewPortUpdated)
    );
    disposables.add(selection.slots.updated.subscribe(this._syncHandles));
    disposables.add(
      selection.slots.transientGeometryUpdated.subscribe(this._scheduleHandles)
    );
    disposables.add(edgelessSlots.readonlyUpdated.subscribe(this._syncHandles));
    disposables.add(
      edgelessSlots.elementResizeEnd.subscribe(this._syncHandles)
    );
    disposables.add(
      edgelessSlots.elementResizeStart.subscribe(() => {
        this._clearSlot('selection');
        this._clearSlot('hover');
      })
    );
    disposables.add(
      effect(() => {
        const value = tool.currentToolOption$.value;
        if (value) this._handleEdgelessToolUpdated(value);
      })
    );
    disposables.add(
      std.store.slots.blockUpdated.subscribe(this._scheduleHandles)
    );
    if (surface) {
      disposables.add(surface.elementUpdated.subscribe(this._scheduleHandles));
      disposables.add(surface.elementRemoved.subscribe(this._syncHandles));
    }
    disposables.add(() => cancelGfxViewportFrame(this.widget.std, 'dom', this));
    this.widget.handleEvent('pointerMove', ctx => {
      this._updateHoverFromPointer(ctx.get('pointerState'));
    });

    this._syncHandles();
  }
}
